import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocumentSettings } from "@/office/word/dokument";

export const lektionDesigns: Lesson = {
  id: "designs",
  title: "Designs und Formatvorlagensätze",
  summary: "Das Aussehen des ganzen Dokuments auf einen Schlag umstellen.",
  durationMinutes: 8,
  learningGoals: [
    "Designs von Formatvorlagensätzen unterscheiden",
    "Ein Design zuweisen",
    "Ein eigenes Design speichern",
  ],
  steps: [
    {
      id: "design-zuweisen",
      title: "Designs",
      explanation: [
        "Ein Design ist die oberste Ebene der Gestaltung. Es legt drei Dinge fest: einen " +
          "Farbsatz, ein Schriftartenpaar (eine für Überschriften, eine für Fließtext) " +
          "und eine Reihe von Effekten für Formen und Diagramme.",
        "Du findest Designs auf der Registerkarte „Entwurf“ ganz links. Ein Klick auf ein " +
          "anderes Design stellt das komplette Dokument um – vorausgesetzt, du hast mit " +
          "Formatvorlagen gearbeitet. Von Hand formatierter Text bleibt unverändert " +
          "stehen, und genau daran zeigt sich, ob ein Dokument sauber aufgebaut ist.",
        "Rechts daneben liegen die Formatvorlagensätze. Sie ändern nur das Aussehen der " +
          "Formatvorlagen innerhalb des gewählten Designs – etwa, wie groß und wie " +
          "abgesetzt Überschriften erscheinen. Design ist also die Grundfarbe und " +
          "Schriftwahl, der Formatvorlagensatz die Feinabstimmung darauf.",
        "Hast du eine Kombination gefunden, die passt, speicherst du sie über „Entwurf“ " +
          "→ „Designs“ → „Aktuelles Design speichern“ und kannst sie in anderen " +
          "Dokumenten wiederverwenden – oder in eine Dokumentvorlage einbauen.",
      ],
      keyPoints: [
        "Entwurf → Designs: Farben, Schriften und Effekte auf einen Schlag.",
        "Formatvorlagensätze verfeinern das Aussehen innerhalb des Designs.",
        "Von Hand formatierter Text zieht beim Designwechsel nicht mit.",
      ],
      task: {
        instruction:
          "Weise deinem Dokument über „Entwurf“ ein anderes Design zu und beobachte, " +
          "welche Teile mitziehen und welche stehen bleiben.",
        hint: "Am deutlichsten wird der Effekt bei einem Dokument mit Überschriftenvorlagen.",
        check: async () => {
          const settings = await readDocumentSettings();
          if (settings === null) {
            return unsupported(
              "Das verwendete Design ist dem Add-in nur in Word für Windows und Mac " +
                "zugänglich – in Word im Web fehlt die Schnittstelle.",
              "Prüfe selbst unter „Entwurf“, welches Design markiert ist, und markiere den Schritt dann als erledigt.",
            );
          }

          return fromCriteria(
            [
              criterion(
                `Dem Dokument ist ein Design zugewiesen (erkannt: ${settings.themeName ?? "unbekannt"})`,
                settings.themeName !== null && settings.themeName.length > 0,
              ),
            ],
            {
              success: `Design „${settings.themeName}“ ist aktiv – Überschriften und Farben folgen ihm jetzt.`,
              failure: "Es konnte kein Design ermittelt werden.",
              hint: "Wähle unter „Entwurf“ eines der Designs im Katalog aus.",
            },
          );
        },
      },
    },
    {
      id: "rueckblick",
      title: "Rückblick",
      explanation: [
        "Designs und Vorlagen sind die letzte Stufe desselben Gedankens, der sich durch " +
          "den ganzen Kurs zieht: Eigenschaften einmal zentral festlegen statt sie " +
          "wiederholt von Hand zu vergeben.",
        "Zeichen gehören in Formatvorlagen, Formatvorlagen in ein Design, und das Ganze " +
          "in eine Dokumentvorlage. Wer diese Kette einmal aufgebaut hat, erstellt ein " +
          "neues Angebot mit einem Doppelklick statt mit einer halben Stunde Formatieren.",
      ],
      keyPoints: [
        "Vorlage speichern als .dotx, nicht als .docx.",
        "Designs stellen Farben und Schriften des ganzen Dokuments um.",
        "Nur was in Formatvorlagen steckt, zieht beim Designwechsel mit.",
      ],
    },
  ],
};
